import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Layers,
  Extension,
  ContentCopy,
  Close,
  ShowChart,
  DragIndicator,
  PlaylistAdd,
} from '@mui/icons-material';
import { Reorder, useDragControls } from 'framer-motion';
import { useSequences, useCreateSequence, useUpdateSequence, useDeleteSequence, useSequence, useSequencePreview, useDuplicateSequence, useSegments, useAddSegmentToSequence, useRemoveSegmentFromSequence, useReorderSequenceSegments } from '../../hooks/useScheduling';
import type { Sequence } from '../../api/types';
import { SectionTabs } from '../../components/SectionTabs';
import { SCHEDULING_TABS, SCHEDULING_ACCENT_COLOR } from './schedulingTabs';
import { schedulingHelp } from '../../components/PageHelp/helpContent';
import { useOrgFilter } from '../../contexts/OrganizationContext';
import { DuplicateToOrgDialog } from '../../components/DuplicateToOrgDialog';

const COLOR_OPTIONS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e', '#10b981',
  '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6',
  '#a855f7', '#d946ef', '#ec4899', '#f43f5e'
];

const SequencesPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingSequence, setEditingSequence] = useState<Sequence | null>(null);
  const [selectedSequenceId, setSelectedSequenceId] = useState<string | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicatingSequence, setDuplicatingSequence] = useState<Sequence | null>(null);

  // Use global organization filter
  const orgFilter = useOrgFilter();

  // Fetch sequences filtered by organization
  const { data: sequences, isLoading, error } = useSequences({
    search: searchQuery || undefined,
    organization_id: orgFilter,
  });

  const createMutation = useCreateSequence();
  const updateMutation = useUpdateSequence();
  const deleteMutation = useDeleteSequence();
  const duplicateMutation = useDuplicateSequence();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#8b5cf6',
    loop_mode: false,
  });

  const handleOpenCreate = () => {
    setFormData({
      name: '',
      description: '',
      color: '#8b5cf6',
      loop_mode: false,
    });
    setEditingSequence(null);
    setCreateDialogOpen(true);
  };

  const handleOpenEdit = (sequence: Sequence) => {
    setFormData({
      name: sequence.name,
      description: sequence.description || '',
      color: sequence.color,
      loop_mode: sequence.loop_mode,
    });
    setEditingSequence(sequence);
    setCreateDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingSequence) {
        await updateMutation.mutateAsync({
          id: editingSequence.id,
          data: formData,
        });
      } else {
        // Backend will assign organization automatically
        await createMutation.mutateAsync(formData);
      }
      setCreateDialogOpen(false);
    } catch (err) {
      console.error('Failed to save sequence:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this sequence?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleOpenDuplicate = (sequence: Sequence) => {
    setDuplicatingSequence(sequence);
    setDuplicateDialogOpen(true);
  };

  const handleDuplicate = async (params: { name?: string; targetOrganizationId?: string }) => {
    if (!duplicatingSequence) return;
    await duplicateMutation.mutateAsync({
      sequenceId: duplicatingSequence.id,
      name: params.name,
      targetOrganizationId: params.targetOrganizationId,
    });
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins} min`;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load sequences: {(error as Error)?.message}
      </Alert>
    );
  }

  return (
    <Box>
      <SectionTabs
        title="Scheduling"
        tabs={SCHEDULING_TABS}
        accentColor={SCHEDULING_ACCENT_COLOR}
        help={schedulingHelp.sequences}
        actionButton={{
          label: 'New Sequence',
          icon: <Add />,
          onClick: handleOpenCreate,
        }}
      />

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search sequences..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          size="small"
        />
      </Paper>

      {/* Sequences Grid */}
      <Grid container spacing={2}>
        {sequences?.map((sequence) => (
          <Grid item xs={12} sm={6} md={4} key={sequence.id}>
            <Card
              sx={{
                cursor: 'pointer',
                border: '2px solid',
                borderColor: sequence.color,
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                },
              }}
              onClick={() => setSelectedSequenceId(sequence.id)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Layers sx={{ color: sequence.color }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                    {sequence.name}
                  </Typography>
                  <Tooltip title="Duplicate to Organization">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDuplicate(sequence);
                      }}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenEdit(sequence);
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(sequence.id);
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>

                {sequence.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {sequence.description}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                  <Chip
                    size="small"
                    icon={<Extension />}
                    label={`${sequence.segment_count} segments`}
                    sx={{ bgcolor: `${sequence.color}20`, color: sequence.color }}
                  />
                  <Chip
                    size="small"
                    label={formatDuration(sequence.total_duration_seconds)}
                  />
                  {sequence.loop_mode && (
                    <Chip size="small" label="Loop" variant="outlined" />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {sequences?.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Layers sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No sequences yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create sequences by combining segments into atmosphere arcs
              </Typography>
              <Button variant="contained" onClick={handleOpenCreate}>
                Create Sequence
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingSequence ? 'Edit Sequence' : 'Create New Sequence'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Color</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {COLOR_OPTIONS.map((color) => (
                  <Box
                    key={color}
                    onClick={() => setFormData({ ...formData, color })}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      bgcolor: color,
                      cursor: 'pointer',
                      border: formData.color === color ? '3px solid white' : 'none',
                      boxShadow: formData.color === color ? `0 0 0 2px ${color}` : 'none',
                    }}
                  />
                ))}
              </Box>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.loop_mode}
                  onChange={(e) => setFormData({ ...formData, loop_mode: e.target.checked })}
                />
              }
              label="Loop mode (repeat sequence continuously)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name || createMutation.isPending || updateMutation.isPending}
          >
            {editingSequence ? 'Save Changes' : 'Create Sequence'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sequence Detail Dialog */}
      {selectedSequenceId && (
        <SequenceDetailDialog
          sequenceId={selectedSequenceId}
          onClose={() => setSelectedSequenceId(null)}
        />
      )}

      {/* Duplicate to Organization Dialog */}
      {duplicatingSequence && (
        <DuplicateToOrgDialog
          open={duplicateDialogOpen}
          onClose={() => {
            setDuplicateDialogOpen(false);
            setDuplicatingSequence(null);
          }}
          entityType="sequence"
          entityId={duplicatingSequence.id}
          entityName={duplicatingSequence.name}
          currentOrganizationId={duplicatingSequence.organization_id}
          onDuplicate={handleDuplicate}
        />
      )}
    </Box>
  );
};

// Draggable Segment Item Component
const DraggableSegmentItem: React.FC<{
  item: any;
  index: number;
  onRemove: (itemId: string) => void;
  isRemoving: boolean;
  formatDuration: (seconds: number) => string;
}> = ({ item, index, onRemove, isRemoving, formatDuration }) => {
  const dragControls = useDragControls();

  return (
    <Reorder.Item
      value={item}
      dragListener={false}
      dragControls={dragControls}
      style={{ listStyle: 'none' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'background.paper',
          mb: 0.5,
          borderRadius: 1,
          p: 1,
          borderLeft: '4px solid',
          borderColor: item.color || '#666',
        }}
      >
        <Box
          onPointerDown={(e) => dragControls.start(e)}
          sx={{ cursor: 'grab', mr: 1, touchAction: 'none' }}
        >
          <DragIndicator sx={{ color: 'text.secondary' }} />
        </Box>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: item.color || '#666',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1.5,
            fontSize: '0.8rem',
            fontWeight: 600,
            color: 'white',
          }}
        >
          {index + 1}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {item.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDuration(item.actual_duration_seconds || 0)} - {item.actual_track_count || 0} tracks
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
          <Chip
            size="small"
            label={`E${item.energy_level || '?'}`}
            sx={{
              bgcolor: `${item.color}30`,
              color: item.color,
              fontWeight: 600,
            }}
          />
          {item.bpm_min && item.bpm_max && (
            <Chip size="small" label={`${item.bpm_min}-${item.bpm_max}`} variant="outlined" />
          )}
        </Box>
        <IconButton
          size="small"
          onClick={() => onRemove(item.id)}
          disabled={isRemoving}
          sx={{ color: 'error.main' }}
        >
          <Delete fontSize="small" />
        </IconButton>
      </Box>
    </Reorder.Item>
  );
};

// Sequence Detail Dialog Component
const SequenceDetailDialog: React.FC<{
  sequenceId: string;
  onClose: () => void;
}> = ({ sequenceId, onClose }) => {
  const { data: sequence, isLoading } = useSequence(sequenceId);
  const { data: preview, refetch: refetchPreview } = useSequencePreview(sequenceId);
  // Fetch ALL segments the user has access to (no org filter)
  const { data: availableSegments } = useSegments({});

  const addSegmentMutation = useAddSegmentToSequence();
  const removeSegmentMutation = useRemoveSegmentFromSequence();
  const reorderMutation = useReorderSequenceSegments();

  const [segments, setSegments] = useState<any[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });

  // Ref for debouncing reorder API calls
  const reorderTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingOrderRef = useRef<any[] | null>(null);

  // Sync segments state with sequence data
  useEffect(() => {
    if (sequence?.segments) {
      setSegments(sequence.segments);
    }
  }, [sequence?.segments]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (reorderTimeoutRef.current) {
        clearTimeout(reorderTimeoutRef.current);
      }
    };
  }, []);

  const handleAddSegment = async (segmentId: string) => {
    try {
      await addSegmentMutation.mutateAsync({
        sequenceId,
        segmentId,
      });
      refetchPreview();
      setSnackbar({ open: true, message: 'Segment added' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to add segment' });
    }
  };

  const handleRemoveSegment = async (itemId: string) => {
    try {
      await removeSegmentMutation.mutateAsync({ sequenceId, itemId });
      refetchPreview();
      setSnackbar({ open: true, message: 'Segment removed' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to remove segment' });
    }
  };

  // Debounced reorder - updates UI immediately but delays API call
  const handleReorder = useCallback((newOrder: any[]) => {
    // Update local state immediately for smooth UI
    setSegments(newOrder);
    pendingOrderRef.current = newOrder;

    // Clear any pending API call
    if (reorderTimeoutRef.current) {
      clearTimeout(reorderTimeoutRef.current);
    }

    // Debounce the API call - only fire after 500ms of no changes
    reorderTimeoutRef.current = setTimeout(async () => {
      const orderToSave = pendingOrderRef.current;
      if (!orderToSave) return;

      try {
        await reorderMutation.mutateAsync({
          sequenceId,
          itemIds: orderToSave.map(s => s.id),
        });
        refetchPreview();
      } catch (err) {
        // Revert on error
        if (sequence?.segments) {
          setSegments(sequence.segments);
        }
        setSnackbar({ open: true, message: 'Failed to reorder segments' });
      }
    }, 500);
  }, [sequenceId, reorderMutation, refetchPreview, sequence?.segments]);

  if (isLoading || !sequence) {
    return (
      <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  const formatDuration = (seconds: number) => {
    if (!seconds) return '0:00';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTotalDuration = (seconds: number) => {
    if (!seconds) return '0 min';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins} min`;
  };

  // Get segments not yet in the sequence for the "add" panel
  const segmentIdsInSequence = new Set(segments.map(s => s.segment_id));
  const segmentsToAdd = availableSegments?.filter(s => !segmentIdsInSequence.has(s.id)) || [];

  // Build energy curve data for visualization
  const energyCurveData = preview?.segments?.map((seg: any) => ({
    name: seg.name,
    energy: seg.energy_level || 5,
    color: seg.color,
    duration: seg.actual_duration_seconds || 0,
  })) || [];

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Layers sx={{ color: sequence.color }} />
            <Typography variant="h6" sx={{ flex: 1 }}>{sequence.name}</Typography>
            <Chip
              label={formatTotalDuration(preview?.total_duration_seconds || 0)}
              size="small"
              sx={{ mr: 1 }}
            />
            <Chip
              label={`${segments.length} segments`}
              size="small"
              variant="outlined"
            />
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Energy Curve Visualization */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShowChart /> Energy Curve
                </Typography>
                {energyCurveData.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      Add segments to see energy curve
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ position: 'relative' }}>
                    {/* Y-axis labels */}
                    <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 20, width: 30, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">10</Typography>
                      <Typography variant="caption" color="text.secondary">5</Typography>
                      <Typography variant="caption" color="text.secondary">1</Typography>
                    </Box>
                    {/* Chart area */}
                    <Box sx={{ ml: 4, height: 120, display: 'flex', alignItems: 'flex-end', gap: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                      {energyCurveData.map((point: any, index: number) => (
                        <Tooltip
                          key={index}
                          title={`${point.name}: Energy ${point.energy}/10, ${formatDuration(point.duration)}`}
                          arrow
                        >
                          <Box
                            sx={{
                              flex: Math.max(1, point.duration / 60), // Width proportional to duration
                              minWidth: 40,
                              height: `${point.energy * 10}%`,
                              bgcolor: point.color || sequence.color,
                              borderRadius: '4px 4px 0 0',
                              transition: 'all 0.3s',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              '&:hover': {
                                opacity: 0.8,
                                transform: 'scaleY(1.02)',
                              },
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'white',
                                fontWeight: 600,
                                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                                writingMode: point.duration > 300 ? 'horizontal-tb' : 'vertical-rl',
                                fontSize: '0.65rem',
                              }}
                            >
                              E{point.energy}
                            </Typography>
                          </Box>
                        </Tooltip>
                      ))}
                    </Box>
                    {/* X-axis labels (segment names) */}
                    <Box sx={{ ml: 4, display: 'flex', gap: 0.5, mt: 0.5 }}>
                      {energyCurveData.map((point: any, index: number) => (
                        <Box
                          key={index}
                          sx={{
                            flex: Math.max(1, point.duration / 60),
                            minWidth: 40,
                            textAlign: 'center',
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: '0.65rem',
                            }}
                          >
                            {point.name}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Segments List */}
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Extension /> Segments ({segments.length})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Drag to reorder
                  </Typography>
                </Box>

                {segments.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Extension sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography color="text.secondary">No segments yet</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Click segments on the right to add them
                    </Typography>
                  </Box>
                ) : (
                  <Reorder.Group
                    axis="y"
                    values={segments}
                    onReorder={handleReorder}
                    style={{ listStyle: 'none', padding: 0, margin: 0 }}
                  >
                    {segments.map((item, index) => (
                      <DraggableSegmentItem
                        key={item.id}
                        item={item}
                        index={index}
                        onRemove={handleRemoveSegment}
                        isRemoving={removeSegmentMutation.isPending}
                        formatDuration={formatDuration}
                      />
                    ))}
                  </Reorder.Group>
                )}
              </Paper>
            </Grid>

            {/* Available Segments */}
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 2, bgcolor: 'background.default', maxHeight: 500, overflow: 'auto' }}>
                <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PlaylistAdd /> Available Segments
                </Typography>
                {segmentsToAdd.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      All segments are in use
                    </Typography>
                  </Box>
                ) : (
                  <List dense>
                    {segmentsToAdd.map((segment) => (
                      <ListItem
                        key={segment.id}
                        sx={{
                          bgcolor: 'background.paper',
                          mb: 0.5,
                          borderRadius: 1,
                          cursor: 'pointer',
                          borderLeft: '3px solid',
                          borderColor: segment.color,
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                        onClick={() => handleAddSegment(segment.id)}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {segment.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {segment.actual_track_count || segment.track_count || 0} tracks - {formatDuration(segment.actual_duration_seconds || 0)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            size="small"
                            label={`E${segment.energy_level || '?'}`}
                            sx={{
                              bgcolor: `${segment.color}30`,
                              color: segment.color,
                              fontWeight: 600,
                              fontSize: '0.7rem',
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddSegment(segment.id);
                            }}
                            disabled={addSegmentMutation.isPending}
                            sx={{ color: 'primary.main' }}
                          >
                            <Add fontSize="small" />
                          </IconButton>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                )}

                {/* Tip for energy flow */}
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.main', borderRadius: 1, opacity: 0.1 }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                    Energy Flow Tip
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    For smooth transitions, keep energy jumps between adjacent segments to 2 levels or less.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </>
  );
};

export default SequencesPage;
